/**
 * Done. / ChiroCPD — In-Memory TTL Cache
 *
 * Lightweight client-side cache for API responses with automatic expiry.
 * Used by useExportFields (30s TTL) and useActivities to avoid redundant requests.
 *
 * Not persistent — cleared on page refresh. Sufficient for single-tab compliance tool.
 */

interface CacheEntry<T> {
  value: T;
  expiresAt: number;
}

const store = new Map<string, CacheEntry<unknown>>();

/**
 * Read a cached value. Returns `undefined` if missing or expired.
 * Caller must cast to the correct type.
 */
export function cacheGet(key: string): unknown {
  const entry = store.get(key);
  if (!entry) return undefined;
  if (Date.now() > entry.expiresAt) {
    store.delete(key);
    return undefined;
  }
  return entry.value;
}

/**
 * Write a value to the cache with a TTL in milliseconds.
 * Default TTL: 30 seconds.
 */
export function cacheSet(key: string, value: unknown, ttlMs = 30_000): void {
  store.set(key, { value, expiresAt: Date.now() + ttlMs });
}

/**
 * Invalidate a specific cache key.
 */
export function cacheInvalidate(key: string): void {
  store.delete(key);
}

/**
 * Invalidate all keys that start with `prefix`.
 * Useful for invalidating related entries (e.g. all export-field keys).
 */
export function cacheInvalidatePrefix(prefix: string): void {
  for (const key of store.keys()) {
    if (key.startsWith(prefix)) {
      store.delete(key);
    }
  }
}

/**
 * Clear entire cache (useful for logout).
 */
export function cacheClear(): void {
  store.clear();
}
